import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Checkbox,
    MenuItem,
    Grid,
    CircularProgress,
} from "@mui/material";
import apiClient from "../../../api/axios";

const BannerFormDialog = ({ open, onClose, banner, onSuccess }) => {
    const isEditMode = Boolean(banner);
    const imageBaseUrl = "https://scale-kuwait.com/";

    const [formData, setFormData] = useState({
        title: "",
        body: "",
        start_time: "",
        end_time: "",
        max_shows: 0,
        priority: 1,
        is_active: true,
        type: "promotional",
    });
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const resolveImageUrl = (url) => {
        if (!url) return "";
        return url.startsWith("http") ? url : `${imageBaseUrl}${url}`;
    };

    useEffect(() => {
        if (banner) {
            // Convert ISO timestamps to datetime-local format if needed
            const formatForInput = (isoStr) => {
                if (!isoStr) return "";
                try {
                    const date = new Date(isoStr);
                    const offset = date.getTimezoneOffset() * 60000;
                    const localISOTime = new Date(date.getTime() - offset)
                        .toISOString()
                        .slice(0, 16);
                    return localISOTime;
                } catch (e) {
                    return isoStr;
                }
            };

            setFormData({
                title: banner.title || "",
                body: banner.body || "",
                start_time: formatForInput(banner.start_time),
                end_time: formatForInput(banner.end_time),
                max_shows: banner.max_shows ?? 0,
                priority: banner.priority ?? 1,
                is_active: banner.is_active ?? true,
                type: banner.type || "promotional",
            });
            setImageFile(null);
            setPreviewUrl(resolveImageUrl(banner.image_url || ""));
        } else {
            // Reset for create mode
            setFormData({
                title: "",
                body: "",
                start_time: "",
                end_time: "",
                max_shows: 0,
                priority: 1,
                is_active: true,
                type: "promotional",
            });
            setImageFile(null);
            setPreviewUrl("");
        }
        setError(null);
    }, [banner, open]);

    const handleChange = (field) => (e) => {
        const value =
            e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];

        // Revoke old blob URL previews to avoid leaks
        if (previewUrl && previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewUrl);
        }

        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);
            setImageFile(file);
        } else {
            setImageFile(null);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            if (!isEditMode && !imageFile) {
                setError("Image is required");
                setLoading(false);
                return;
            }

            const fd = new FormData();
            const startIso = formData.start_time
                ? new Date(formData.start_time).toISOString()
                : "";
            const endIso = formData.end_time
                ? new Date(formData.end_time).toISOString()
                : "";

            fd.append("title", formData.title);
            fd.append("body", formData.body);
            if (startIso) fd.append("start_time", startIso);
            if (endIso) fd.append("end_time", endIso);
            fd.append("max_shows", Number(formData.max_shows));
            fd.append("priority", Number(formData.priority));
            // Backend expects boolean-like True/False, not lowercase strings
            fd.append("is_active", formData.is_active ? "True" : "False");
            fd.append("type", formData.type);
            if (imageFile) {
                fd.append("image", imageFile);
            }

            if (isEditMode) {
                fd.append("id", banner.id);
                await apiClient.put("/banners/update/", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                await apiClient.post("/banners/create/", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            onSuccess();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save banner");
        } finally {
            setLoading(false);
        }
    };

    // Cleanup blob previews on unmount
    useEffect(() => {
        return () => {
            if (previewUrl && previewUrl.startsWith("blob:")) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEditMode ? "Edit Banner" : "Create New Banner"}</DialogTitle>
            <DialogContent>
                {error && (
                    <div style={{ color: "red", marginBottom: "16px" }}>{error}</div>
                )}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            label="Title"
                            fullWidth
                            value={formData.title}
                            onChange={handleChange("title")}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Body"
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.body}
                            onChange={handleChange("body")}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="outlined" component="label">
                            Upload Image
                            <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                        </Button>
                        {imageFile && (
                            <span style={{ marginLeft: "12px" }}>{imageFile.name}</span>
                        )}
                        {!imageFile && previewUrl && (
                            <span style={{ marginLeft: "12px" }}>Using existing image</span>
                        )}
                    </Grid>
                    {previewUrl && (
                        <Grid item xs={12}>
                            <img
                                src={previewUrl}
                                alt="Banner Preview"
                                style={{ width: "100%", maxHeight: "200px", objectFit: "cover", borderRadius: "4px", border: "1px solid #eee" }}
                            />
                        </Grid>
                    )}
                    <Grid item xs={6}>
                        <TextField
                            label="Start Time"
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={formData.start_time}
                            onChange={handleChange("start_time")}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="End Time"
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={formData.end_time}
                            onChange={handleChange("end_time")}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            label="Max Shows (0 = Always)"
                            type="number"
                            fullWidth
                            value={formData.max_shows}
                            onChange={handleChange("max_shows")}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            label="Priority"
                            type="number"
                            fullWidth
                            value={formData.priority}
                            onChange={handleChange("priority")}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            label="Type"
                            select
                            fullWidth
                            value={formData.type}
                            onChange={handleChange("type")}
                        >
                            <MenuItem value="promotional">Promotional</MenuItem>
                            <MenuItem value="notice">Notice</MenuItem>
                            <MenuItem value="alert">Alert</MenuItem>
                            <MenuItem value="info">Info</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.is_active}
                                    onChange={handleChange("is_active")}
                                />
                            }
                            label="Active"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={20} />}
                >
                    {isEditMode ? "Update" : "Create"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BannerFormDialog;
